import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import type {
  TrainingParticipant,
  TrainingParticipantChapter,
} from "@repo/app-types/entities/trainingParticipant";
import { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { Header, Heading } from "react-aria-components";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { useAllTrainingParticipantsForPlaybookTeammateQuery } from "../../../api/entities/trainingParticipant.js";
import { appPaths } from "../../../app/appPaths.js";
import { AssessmentCell } from "../../trainingParticipants/assessmentCell.js";

export interface TrainingParticipantsSectionProps {
  className?: string;
  playbookId: PlaybookId;
  teammateId: TeammateId;
}

const RECENT_PARTICIPANT_DAYS_THRESHOLD = 30;

export const TrainingParticipantsSection: FC<
  TrainingParticipantsSectionProps
> = (props) => {
  const navigate = useNavigate();

  const { data: participants, isLoading } =
    useAllTrainingParticipantsForPlaybookTeammateQuery({
      playbookId: props.playbookId,
      teammateId: props.teammateId,
    });

  const participantsWithRecentActivity = useMemo(
    () =>
      participants?.filter((participant) => {
        const lastActivityDate = DateTime.fromISO(participant.last_activity_at);
        return (
          DateTime.now().diff(lastActivityDate, "days").days <=
          RECENT_PARTICIPANT_DAYS_THRESHOLD
        );
      }),
    [participants],
  );

  if (
    !participantsWithRecentActivity ||
    participantsWithRecentActivity.length === 0
  ) {
    return null;
  }

  const onParticipantAction = (
    participant: TrainingParticipant,
    chapter: TrainingParticipantChapter,
  ) => {
    const query = new URLSearchParams({
      chapterId: chapter.id,
    });

    navigate(
      `${appPaths.teammateTraining.build({
        playbookId: props.playbookId,
        teammateId: props.teammateId,
        participantId: participant.id,
      })}?${query.toString()}`,
    );
  };

  return (
    <section className={twMerge(props.className, "space-y-2")}>
      <Heading level={2} className="text-base font-semibold text-foreground">
        Recent trainings
      </Heading>

      <div className="space-y-4 rounded-lg border border-border-light bg-white p-4">
        {participantsWithRecentActivity.map((participant) => (
          <section key={participant.id} className="space-y-2">
            <Header className="flex items-end gap-2">
              <Heading
                level={3}
                className="text-sm font-medium text-foreground"
              >
                {participant.course.name}
              </Heading>

              <div className="text-sm text-muted-foreground">
                {DateTime.fromISO(participant.last_activity_at).toRelative()}
              </div>
            </Header>

            <AssessmentCell
              participant={participant}
              onParticipantAction={onParticipantAction}
            />
          </section>
        ))}
      </div>
    </section>
  );
};
